import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  CreditCard, Monitor, Globe, Phone, ShoppingCart, Landmark,
  ArrowRight, Shield, Clock, Users, Zap, HeartHandshake,
  CheckCircle, ChevronRight, Star,
} from "lucide-react";
import AnimatedCounter from "@/components/home/AnimatedCounter";
import GoogleReviewsBanner, { GoogleG } from "@/components/home/GoogleReviewsBanner";
import BCorpMark from "@/components/BCorpMark";
import { fetchGoogleReviews } from "@/utils/googleReviews";
import { BCORP_CERTIFIED, BCORP_IMPACT_SCORE } from "@/utils/bcorp";
import SavingsComparisonHeroVisual from "@/components/hero/SavingsComparisonHeroVisual";
import SavingsCalculator from "@/components/SavingsCalculator";
import ContactForm from "@/components/ContactForm";
import SEOHead from "@/components/SEOHead";
import WhatsAppButton from "@/components/WhatsAppButton";
import analytics from "@/utils/analytics";
import { fadeUp, fadeRight, scaleIn, stagger, viewportOnce, viewportOnceSmall, viewportOnceOnly, statItemInitial, statItemAnimate, statItemTransition, hoverLiftSmall, hoverLiftMedium, tapScale, tapScaleSmall } from "@/utils/motionVariants";

// Official Living Wage Foundation artwork from the brand CDN, unmodified.
const LIVING_WAGE_MARK = "https://cdn.candopayments.com/certifications/living-wage-employer_colour_web_transparent.png";

// Verbatim from PaymentsBeyondPurposePage's HERO_PARAS — the homepage must not
// originate certification claims that page does not already make.
const PBP_HOME_PARAS = [
  "CanDo Payments is a Certified B Corporation™ and an Accredited Living Wage Employer — independent proof of how we've always tried to run this business: transparent, fair and genuinely people-first.",
  "We help UK businesses take payments simply and securely, while proving that competitive pricing, great service and responsible business can go together.",
];

const CLIENT_LOGOS = [
  { src: "https://cdn.candopayments.com/clients/pendergasts_colour_web_solid.jpg", alt: "Pendergasts" },
  { src: "https://cdn.candopayments.com/clients/porta-tapas_colour_web_solid.jpg", alt: "Porta Chester" },
  { src: "https://cdn.candopayments.com/clients/invisible-wind-factory_colour_web_solid.jpg", alt: "Invisible Wind Factory" },
  { src: "https://cdn.candopayments.com/clients/chester-fc_colour_web_solid.jpg", alt: "Chester FC" },
  { src: "https://cdn.candopayments.com/clients/pop-specs_colour_web_solid.jpg", alt: "Pop Specs" },
  { src: "https://cdn.candopayments.com/clients/miss-attire_colour_web_solid.jpg", alt: "Miss Attire" },
  { src: "https://cdn.candopayments.com/clients/plas-gwyn_colour_web_solid.jpg", alt: "Plas Gwyn" },
  { src: "https://cdn.candopayments.com/clients/cavern-club_colour_web_solid.png", alt: "Cavern Club" },
  { src: "https://cdn.candopayments.com/clients/raj-aesthetics_colour_web_solid.jpg", alt: "Raj Aesthetics" },
  { src: "https://cdn.candopayments.com/clients/pont-bakery_colour_web_solid.jpg", alt: "Pont Bakery" },
  { src: "https://cdn.candopayments.com/clients/the-chester-fields_colour_web_solid.jpg", alt: "The Chesterfields" },
];

const STATS = [
  { value: 4, suffix: " million+", label: "Transactions in last 12 months" },
  { value: 99, suffix: ".9%", label: "Payment Uptime" },
  { value: 4, suffix: " min", label: "Avg. Support Response" },
  { value: 15, suffix: "+ yrs", label: "Industry Experience" },
];

const PROBLEMS = [
  { problem: "Hidden fees & confusing contracts", solution: "Transparent pricing with no surprises", icon: Landmark },
  { problem: "Slow support & endless ticket queues", solution: "Real UK humans with <4 min response time", icon: Clock },
  { problem: "Unreliable terminals & dropped connections", solution: "Fast, secure tech backed by Elavon", icon: Zap },
];

const SERVICES = [
  { icon: CreditCard, title: "Card Machines", desc: "Never lose a sale — reliable terminals with sub-2-second contactless, next-day delivery, and zero lock-in.", link: "/card-machines", color: "bg-brand-aqua-light text-brand-aqua", itemName: "card_machines" },
  { icon: Monitor, title: "EPOS Systems", desc: "Run your whole business from one screen — sales, stock, staff and reporting in real time.", link: "/epos-systems", color: "bg-brand-pink-light text-brand-pink", itemName: "epos" },
  { icon: Globe, title: "Online Payments", desc: "Reduce cart abandonment with a fast, secure checkout your customers trust. Live in 24 hours.", link: "/online-payments", color: "bg-brand-aqua-light text-brand-aqua-dark", itemName: "online_payments" },
  { icon: Phone, title: "Virtual Terminal", desc: "Take card payments over the phone from any device — no hardware needed, no minimum contract.", link: "/virtual-terminal", color: "bg-blue-50 text-brand-light-blue", itemName: "phone_payments" },
  { icon: ShoppingCart, title: "Online Ordering", desc: "Let customers order and pay online — synced to your EPOS so nothing gets missed.", link: "/online-ordering", color: "bg-amber-50 text-amber-600", itemName: "online_ordering" },
  { icon: Landmark, title: "Merchant Cash Advance", desc: "Get business funding repaid flexibly through card sales — no fixed payments, no hidden fees.", link: "/merchant-cash-advance", color: "bg-brand-aqua-light text-brand-aqua-dark", itemName: "merchant_cash_advance" },
];

const STEPS = [
  { step: "01", title: "Tell us about your business", desc: "Fill in a 2-minute form or give us a call. We'll ask about your turnover, current provider, and what's not working." },
  { step: "02", title: "Get a side-by-side comparison", desc: "We'll review your current charges and send you a clear breakdown showing exactly what you'd pay with CanDo." },
  { step: "03", title: "Go live — we handle everything", desc: "We set up your new equipment, migrate your account, and train your staff. Order before 2pm for next-day delivery." },
];

const WHY_ITEMS = [
  { icon: Shield, title: "Pricing you can actually read", desc: "No hidden fees, no fine print. Your monthly statement matches your quote — every time." },
  { icon: Zap, title: "Backed by Elavon", desc: "Your payments run on the same infrastructure trusted by Hilton, IKEA, and Virgin Atlantic." },
  { icon: HeartHandshake, title: "Support in under 4 minutes", desc: "Message us on WhatsApp, call the freephone, or email. A real person answers — not a chatbot.", cta: { placement: "home_support", label: "Message us on WhatsApp" } },
  { icon: Clock, title: "Live by tomorrow", desc: "Order before 2pm and your terminal arrives next working day. We handle setup and migration." },
  { icon: Users, title: "Family-run, client-focused", desc: "We're a small team that knows your name. Every account gets a dedicated contact — not a ticket number." },
];

const SAVINGS_CHECKS = [
  "No hidden fees or lock-in contracts",
  "Rates based on your actual transaction volume",
  "Free terminal swap and full migration support",
];

function HeroSection() {
  return (
    <section className="hero-gradient relative" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-5">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua">The B Corp™ Certified Payments Company</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-brand-black tracking-tight leading-[1.08]">
              Stop overpaying for <span className="gradient-text">card payments.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed max-w-lg">
              Join 100s of UK businesses paying less for card processing, with real human support and technology that just works. Get your free savings comparison in 60 seconds.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3">
              <Link to="/switch-and-save#calculator" className="cta-primary text-white font-semibold rounded-full px-8 py-4 text-sm flex items-center justify-center gap-2 btn-shine pulse-glow active:scale-95 transition-transform" data-testid="hero-cta-quote">See How Much I&apos;d Save <ArrowRight size={16} /></Link>
              <Link to="/overpaying-scorecard" className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-4 text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform" data-testid="hero-cta-scorecard">Am I Overpaying?</Link>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-brand-black pt-1">
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> Avg. saving: £186/mo*</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> 4 min support response</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> 99.9% uptime</span>
            </motion.div>
          </motion.div>
          <div className="hidden lg:block">
            <SavingsComparisonHeroVisual />
          </div>
        </div>
      </div>
    </section>
  );
}

// react-fast-marquee renders nothing until its own mount effect runs, but the
// react-snap snapshot contains the fully mounted marquee. Hydrating that leaves
// unconsumed DOM nodes, which React 19 reports as a hydration failure (#418)
// on every home-page load and regenerates the whole tree (duplicating the
// helmet head tags). So both the prerender and the first client render show a
// static logo row, and the marquee is swapped in after mount. Under react-snap
// the swap never happens, keeping the snapshot identical to what hydrates.
const IS_PRERENDER =
  typeof navigator !== "undefined" &&
  navigator.userAgent.indexOf("ReactSnap") !== -1;

function ClientLogos() {
  const [marqueeReady, setMarqueeReady] = useState(false);
  useEffect(() => {
    if (!IS_PRERENDER) setMarqueeReady(true);
  }, []);
  const logos = CLIENT_LOGOS.map((logo) => (
    <img key={logo.alt} src={logo.src} alt={logo.alt} className="h-10 sm:h-12 mx-6 sm:mx-10 object-contain" />
  ));
  return (
    <section className="py-8 bg-white border-y border-brand-light-grey/40" data-testid="client-logos-section">
      <div className="max-w-7xl mx-auto px-4">
        <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-brand-grey/60 mb-6">Trusted by businesses across the UK</p>
        {/* min-h reserves the row height while the marquee mounts (no layout shift). */}
        <div className="min-h-10 sm:min-h-12">
          {marqueeReady ? (
            <Marquee speed={25} gradient gradientColor="#ffffff" gradientWidth={100} className="logo-marquee">
              {logos}
            </Marquee>
          ) : (
            <div className="logo-marquee flex items-center overflow-hidden" data-testid="client-logos-static">
              {logos}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Credentials band — companion to the client carousel above it, deliberately
 * on its own tint because certifications are not customers.
 *
 * The B Corp mark sets the geometry: 72px minimum (80px from sm up), and every
 * gap here clears half the mark's width on all sides, per B Lab's clear-space
 * rule. Sizing anything down to fit is not an option — the row wraps instead.
 */
function CredentialsStrip() {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Module-level cached in utils/googleReviews — shares the single
    // /api/google-reviews call with GoogleReviewsBanner further down the page.
    fetchGoogleReviews().then((d) => { if (!cancelled) setReviews(d); });
    return () => { cancelled = true; };
  }, []);

  const rating = reviews?.place?.rating;
  const ratingDisplay = rating ? Number(rating).toFixed(1) : "4.8";

  return (
    <section className="py-10 sm:py-12 bg-gray-50 border-b border-brand-light-grey/40" data-testid="credentials-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-brand-grey/60 mb-8">Independently certified &amp; accredited</p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-8 sm:gap-x-14">
          <BCorpMark variant="black" className="sm:h-20" testId="credentials-bcorp-mark" />

          <img
            src={LIVING_WAGE_MARK}
            alt="Accredited Living Wage Employer"
            width="825"
            height="678"
            className="h-14 sm:h-16 w-auto shrink-0"
          />

          {/* Text treatment — no official Better Business Act artwork on the
              brand CDN, and a third-party logo is never recreated by hand. */}
          <div className="text-center">
            <p className="font-heading font-bold text-brand-black text-sm">Better Business Act</p>
            <p className="text-xs text-brand-grey mt-0.5">Proud Supporter</p>
          </div>

          <div className="flex items-center gap-2.5">
            <GoogleG size={22} />
            <span className="flex items-center gap-1">
              <Star size={16} className="fill-amber-400 text-amber-400" />
              <span className="font-heading text-lg font-bold text-brand-black leading-none">{ratingDisplay}</span>
            </span>
            <span className="text-xs text-brand-grey">Google rating</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-20 bg-brand-black text-white relative overflow-hidden" data-testid="stats-section">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #69c0ab 0%, transparent 50%), radial-gradient(circle at 80% 50%, #df65a2 0%, transparent 50%)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {STATS.map((stat) => (
            <motion.div key={stat.label} initial={statItemInitial} whileInView={statItemAnimate} viewport={viewportOnceOnly} transition={statItemTransition} className="space-y-1">
              <p className="text-4xl sm:text-5xl font-heading font-bold stat-shine"><AnimatedCounter target={stat.value} suffix={stat.suffix} /></p>
              <p className="text-sm text-brand-grey">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProblemSolutionSection() {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="problem-solution-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-8 md:mb-16">
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Why CanDo?</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black tracking-tight">Worried about <span className="gradient-text-pink">hidden fees</span> and <span className="gradient-text-pink">poor support</span>?</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROBLEMS.map((item) => (
            <motion.div key={item.solution} variants={fadeUp} whileHover={hoverLiftSmall} className="bg-white border border-brand-light-grey rounded-2xl p-5 sm:p-8 service-card">
              <div className="w-12 h-12 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-5"><item.icon size={22} className="text-brand-aqua" /></div>
              <p className="text-sm font-semibold text-brand-pink line-through decoration-brand-pink/40 mb-2">{item.problem}</p>
              <p className="text-base font-bold text-brand-black">{item.solution}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section className="py-16 md:py-24 bg-gray-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-8 md:mb-16">
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Our Solutions</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black tracking-tight">One provider, every payment type</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service) => (
            <motion.div key={service.title} variants={fadeUp} whileHover={hoverLiftMedium} whileTap={tapScale}>
              <Link to={service.link} onClick={() => analytics.productClicked(service.itemName, { source: "/" })} className="block bg-white border border-brand-light-grey rounded-2xl p-5 sm:p-8 h-full service-card group" data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`} data-measurement-owned="product">
                <div className={`w-12 h-12 rounded-xl ${service.color} flex items-center justify-center mb-5 transition-transform group-hover:scale-110`}><service.icon size={22} /></div>
                <h3 className="font-heading text-lg font-bold text-brand-black mb-2 group-hover:text-brand-aqua transition-colors">{service.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed mb-4">{service.desc}</p>
                <span className="text-sm font-semibold text-brand-aqua flex items-center gap-1 group-hover:gap-2.5 transition-all">See how it works <ChevronRight size={14} /></span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function PaymentFinderBanner() {
  return (
    <section className="py-8 sm:py-16 bg-brand-aqua-light" data-testid="payment-finder-banner">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/tools" className="group block bg-white border-2 border-brand-aqua/20 rounded-2xl p-5 sm:p-8 hover:border-brand-aqua hover:shadow-lg transition-all duration-300" data-testid="homepage-finder-cta">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-brand-aqua flex items-center justify-center flex-shrink-0">
                <Zap size={22} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua mb-1">Free for every business</p>
                <h3 className="font-heading text-lg sm:text-2xl font-bold text-brand-black mb-1">Explore our payment tools</h3>
                <p className="text-sm text-brand-grey leading-relaxed">Calculators, scorecards and comparison tools to help you understand your costs — no sign-up required.</p>
              </div>
              <span className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3 text-sm flex items-center gap-2 transition-colors btn-shine flex-shrink-0 group-hover:gap-3">
                Explore free tools <ArrowRight size={16} />
              </span>
            </div>
          </Link>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section className="py-16 md:py-24 bg-gray-50" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Getting Started</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black tracking-tight">Switching is simpler than you think</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {STEPS.map((item, i) => (
            <motion.div key={item.step} variants={fadeUp} className="relative text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-brand-aqua text-white font-heading text-xl md:text-2xl font-bold flex items-center justify-center mx-auto mb-3 md:mb-5 transition-transform hover:scale-110 hover:rotate-3">{item.step}</div>
              <h3 className="font-heading text-lg md:text-xl font-bold text-brand-black mb-2 md:mb-3">{item.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed max-w-xs mx-auto">{item.desc}</p>
              {i < 2 && <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-brand-light-grey" />}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function WhyChooseUsSection() {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Why CanDo Payments</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-5 md:mb-8">Why 100s of businesses chose CanDo</motion.h2>
            <div className="space-y-4 md:space-y-5">
              {WHY_ITEMS.map((item) => (
                <motion.div key={item.title} variants={fadeRight} className="flex gap-4 items-start group">
                  <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0 transition-all group-hover:bg-brand-aqua group-hover:scale-110">
                    <item.icon size={18} className="text-brand-aqua group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-brand-black mb-1">{item.title}</h3>
                    <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
                    {item.cta && (
                      <WhatsAppButton
                        variant="link"
                        placement={item.cta.placement}
                        label={item.cta.label}
                        iconSize={14}
                        className="mt-2 text-sm"
                      />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceOnly} variants={scaleIn}>
            <img src="https://cdn.candopayments.com/marketing/heroes/why-choose-us_colour_web_solid.webp" alt="Card payment terminal" width="6000" height="4000" className="rounded-2xl shadow-xl w-full h-auto" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/**
 * Mid-page values moment. The mark links out to the B Lab directory profile
 * (third-party verification); the internal story link sits on the text below
 * it, never on the mark — spec §1.
 *
 * Copy is lifted verbatim from PaymentsBeyondPurposePage so the homepage cannot
 * drift into claims that page does not make. The score and date are published
 * by B Lab on the profile.
 */
function PaymentsBeyondPurposeSection() {
  return (
    <section className="py-16 md:py-20 bg-white border-t border-brand-light-grey/40" data-testid="home-payments-beyond-purpose">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
          {/* mb-8 clears half the mark's width; section padding clears the rest */}
          <motion.div variants={fadeUp} className="flex justify-center mb-8">
            <BCorpMark variant="black" testId="home-pbp-bcorp-mark" />
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-5">
            Purpose Beyond Payments
          </motion.h2>
          {PBP_HOME_PARAS.map((p) => (
            <motion.p key={p.slice(0, 28)} variants={fadeUp} className="text-brand-grey leading-relaxed mb-4">{p}</motion.p>
          ))}
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mt-6 mb-7">
            {`Certified ${BCORP_CERTIFIED} · B Impact Score ${BCORP_IMPACT_SCORE}`}
          </motion.p>
          <motion.div variants={fadeUp}>
            <Link
              to="/purpose-beyond-payments"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-aqua-dark hover:text-brand-black transition-colors group"
              data-testid="home-pbp-text-link"
            >
              See how we use payments for good
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function CalculatorSection() {
  return (
    <section id="calculator" className="py-16 md:py-24 bg-white" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Switch & Save</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-4">Find out what you could save in 30 seconds</motion.h2>
            <motion.p variants={fadeUp} className="text-brand-grey leading-relaxed mb-6">Enter your current charges below. Our calculator compares them against CanDo rates and shows you an instant estimate — no sign-up needed.</motion.p>
            <motion.div variants={fadeUp} className="space-y-3">
              {SAVINGS_CHECKS.map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-brand-black"><CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}</div>
              ))}
            </motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceOnly} variants={scaleIn}><SavingsCalculator /></motion.div>
        </div>
      </div>
    </section>
  );
}

function FinalCTASection() {
  return (
    <section id="contact" className="cta-section-bg py-16 md:py-24 relative" data-testid="final-cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="text-white space-y-6">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/60">Takes 2 minutes</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Get your free savings comparison</h2>
            <p className="text-white/80 leading-relaxed">Tell us what you&apos;re paying now. We&apos;ll send you a side-by-side comparison of your current costs vs CanDo pricing within 24 hours. No obligation. No hard sell.</p>
            <div className="bg-white/10 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-white">What you&apos;ll get:</p>
              <div className="flex items-center gap-2 text-sm text-white/90"><CheckCircle size={14} className="text-white/60 flex-shrink-0" /> Line-by-line cost comparison with your current provider</div>
              <div className="flex items-center gap-2 text-sm text-white/90"><CheckCircle size={14} className="text-white/60 flex-shrink-0" /> Your estimated monthly and annual savings</div>
              <div className="flex items-center gap-2 text-sm text-white/90"><CheckCircle size={14} className="text-white/60 flex-shrink-0" /> A no-pressure recommendation for your setup</div>
            </div>
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3"><Phone size={18} className="text-white/60" /><div><p className="text-sm text-white/60">Call us free</p><a href="tel:+448003682989" className="text-lg font-heading font-bold hover:text-white/80 transition-colors" data-testid="cta-phone">0800 368 2989</a></div></div>
              <div className="flex items-center gap-3"><Clock size={18} className="text-white/60" /><div><p className="text-sm text-white/60">Mon-Fri 9am-5pm</p><p className="text-sm font-semibold">Most businesses save £150–£300/month*</p></div></div>
            </div>
            <p className="text-[10px] text-white/40 pt-2">*Based on analysis of customer statements. Individual results may vary.</p>
          </div>
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl"><ContactForm /></div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div>
      <SEOHead title="Merchant Service Provider in the UK" description="CanDo Payments is the go-to provider for reliable UK merchant services. We help businesses accept payments seamlessly with secure, fast and scalable solutions." path="/" />
      <HeroSection />
      <ClientLogos />
      <CredentialsStrip />
      <StatsSection />
      <ProblemSolutionSection />
      <ServicesSection />
      <PaymentFinderBanner />
      <HowItWorksSection />
      <WhyChooseUsSection />
      <PaymentsBeyondPurposeSection />
      {/* Testimonials moved ABOVE the final CTA — social proof primes the
          ask. Cold traffic needs to see real voices before submitting
          their details, not after. */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-10">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Trusted by UK Businesses</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black tracking-tight">See what our customers say</motion.h2>
          </motion.div>
          <GoogleReviewsBanner />
        </div>
      </section>
      <CalculatorSection />
      <FinalCTASection />
    </div>
  );
}
